import { ApiTagTypes, baseApi, IPaginatedResponse, rewriteUrlIfDev } from './baseApi';
import ModuleOutputV2 from '@biolibtech/biolib-js/src/AppClient/biolibBinaryFormat/ModuleOutputV2';
import ModuleInput, {
    MODULE_INPUT_HEADER_SIZE,
} from '@biolibtech/biolib-js/src/AppClient/biolibBinaryFormat/ModuleInput';
import { processStreamedOutput } from '../features/appRun/utils';
import { BioLibSingleton, IJob, JobState } from '@biolibtech/biolib-js';
import { base64ToByteArray } from '@biolibtech/biolib-js/src/AppClient/utils';
import StdoutAndStderrPackage from '@biolibtech/biolib-js/src/AppClient/biolibBinaryFormat/StdoutAndStderrPackage';
import { IResult } from '../features/results/components/types';
import { convertJobToResult } from '../features/results/utils';
import notificationActions from '../state/notification/notificationActions';
import { IResultNote, ResultNoteType } from '../features/results/types';
import { formatLogMesssageWithTimestamp, saveFileAs } from '@utils';
import { navigate } from 'gatsby';

export interface ILogsResponse {
    job: IJob;
    logs: string;
    progress: number;
    systemLogs: string;
}

const textDecoder = new TextDecoder('utf-8', { fatal: true });

async function fetchByteRange(url: string, start: number, end: number): Promise<Uint8Array> {
    const response = await fetch(url, {
        headers: { Range: `bytes=${start}-${end}` },
    });
    if (!response.ok && response.status !== 206) {
        throw new Error(`Failed to fetch byte range: ${response.status} ${response.statusText}`);
    }
    const data = await response.arrayBuffer();
    return new Uint8Array(data);
}

const resultsApi = baseApi.injectEndpoints({
    overrideExisting: false,
    endpoints: (build) => ({
        deleteResult: build.mutation<{}, { jobUuid: string }>({
            invalidatesTags: [ApiTagTypes.result, ApiTagTypes.experiments, ApiTagTypes.experimentResults],
            queryFn: async (args, api, extraOptions, baseQuery) => {
                const response = await baseQuery({
                    url: `/jobs/${args.jobUuid}/`,
                    method: 'DELETE',
                });
                if (response.error) {
                    api.dispatch(
                        notificationActions.addError(`Failed to delete result\nHit error: ${response.error.data}`),
                    );
                    return { error: response.error };
                }
                api.dispatch(notificationActions.addSuccess('Result deleted successfully'));
                return { data: {} };
            },
        }),
        cancelResult: build.mutation<{}, { jobUuid: string; jobAuthToken?: string | null }>({
            invalidatesTags: [ApiTagTypes.result, ApiTagTypes.experiments],
            queryFn: async (args, api, extraOptions, baseQuery) => {
                const response = await baseQuery({
                    url: `/jobs/${args.jobUuid}/`,
                    method: 'PATCH',
                    body: { state: JobState.cancelled },
                    headers: args.jobAuthToken ? { 'Job-Auth-Token': args.jobAuthToken } : {},
                });
                if (response.error) {
                    api.dispatch(
                        notificationActions.addError(`Failed to cancel job\nHit error: ${response.error.data}`),
                    );
                    return { error: response.error };
                }
                return { data: {} };
            },
        }),
        getResult: build.query<IResult, { resultUuid: string; resultAuthToken: string | null }>({
            providesTags: [ApiTagTypes.result],
            query: (args) => ({
                url: `/jobs/${args.resultUuid}/storage/results/download/`,
                headers: args.resultAuthToken ? { 'Job-Auth-Token': args.resultAuthToken } : {},
            }),
            transformResponse: (response: { job: IJob; presigned_download_url: string }) => {
                const { job, presigned_download_url } = response;
                const userUuid = BioLibSingleton.get().httpClient.getSignedInUserId();
                const result = convertJobToResult(job, userUuid, presigned_download_url);
                return result;
            },
        }),
        getInput: build.query<{ inputArguments: string[] }, { jobUuid: string; jobAuthToken: string }>({
            providesTags: [ApiTagTypes.result],
            queryFn: async (args, api, extraOptions, baseQuery) => {
                const { jobUuid, jobAuthToken } = args;
                const jobResultUrlResponse = await baseQuery({
                    headers: { 'Job-Auth-Token': jobAuthToken },
                    url: `/jobs/${jobUuid}/storage/results/download/`,
                });
                if (jobResultUrlResponse.error) {
                    return constructError(JSON.stringify(jobResultUrlResponse.error.data));
                }
                try {
                    const { presigned_input_download_url } = jobResultUrlResponse.data as {
                        presigned_download_url: string;
                        presigned_input_download_url: string;
                        job: IJob;
                    };
                    const inputUrl = rewriteUrlIfDev(presigned_input_download_url);
                    const headerData = await fetchByteRange(inputUrl, 0, MODULE_INPUT_HEADER_SIZE - 1);
                    const { stdinLength, argumentDataLength } = ModuleInput.parseHeader(headerData);
                    let inputArguments: string[] = [];
                    if (argumentDataLength > 0) {
                        const argumentsStart = MODULE_INPUT_HEADER_SIZE + stdinLength;
                        const argumentsEnd = argumentsStart + argumentDataLength - 1;
                        const argumentsData = await fetchByteRange(inputUrl, argumentsStart, argumentsEnd);
                        inputArguments = ModuleInput.parseArgumentsFromData(argumentsData);
                    }
                    return { data: { inputArguments } };
                } catch (error) {
                    console.error(error);
                    return constructError(`Failed to get input. Got error: ${error}`);
                }
            },
        }),
        getLogs: build.query<ILogsResponse, { jobUuid: string; jobAuthToken: string; isResultAvailable?: boolean }>({
            queryFn: async (args, api, extraOptions, baseQuery) => {
                const { jobUuid, jobAuthToken } = args;
                const response = await baseQuery({
                    headers: { 'Job-Auth-Token': jobAuthToken },
                    url: `/jobs/${jobUuid}/storage/results/download/`,
                });

                if (response.error) {
                    return { error: response.error };
                }

                interface IResponse {
                    job: IJob;
                    presigned_download_url: string;
                }

                const { job, presigned_download_url } = response.data as IResponse;
                let logs: string = '';
                if (
                    args.isResultAvailable ||
                    (job.state !== JobState.awaitingInput &&
                        job.state !== JobState.inProgress &&
                        job.state !== JobState.cancelled)
                ) {
                    try {
                        const moduleOutput = new ModuleOutputV2(rewriteUrlIfDev(presigned_download_url));
                        const stdoutText = textDecoder.decode(await moduleOutput.getStdout());
                        const stderrText = textDecoder.decode(await moduleOutput.getStderr());
                        logs = processStreamedOutput(stdoutText.concat(stderrText ? `\n${stderrText}` : ''));
                    } catch (error) {
                        return constructError('Failed to load logs.');
                    }
                }

                return { data: { job, logs, progress: 0.07, systemLogs: '' } };
            },
            onCacheEntryAdded: async (args, { updateCachedData, cacheDataLoaded, cacheEntryRemoved, dispatch }) => {
                let interval: ReturnType<typeof setInterval>;
                try {
                    // wait for the initial query to resolve before proceeding
                    const response = await cacheDataLoaded;
                    let { job } = response.data;
                    if (job.ended_at) {
                        return;
                    }
                    let rawLogs: string = '';

                    let jobFetchCount = 0;
                    let logsFetchCount = 0;
                    let isJobStarted = !!(job.cloud_job && job.cloud_job.started_at);
                    let isPolling = false;
                    const pollComputeNode = async () => {
                        try {
                            if (isPolling) {
                                return;
                            }
                            isPolling = true;

                            if (!isJobStarted) {
                                const response = await fetch(`${location.origin}/api/jobs/${job.public_id}/`, {
                                    headers: { 'Job-Auth-Token': args.jobAuthToken },
                                });
                                const updatedJob = (await response.json()) as IJob;
                                if (updatedJob.cloud_job && updatedJob.cloud_job.started_at) {
                                    jobFetchCount += 1;
                                    isJobStarted = true;
                                    job = updatedJob;
                                    updateCachedData((draft) => {
                                        draft.job = updatedJob;
                                    });
                                } else {
                                    const message =
                                        jobFetchCount === 0
                                            ? 'Starting a compute node, this might take a few minutes.'
                                            : 'Server capacity is being allocated. Please wait...';

                                    if (jobFetchCount % 5 === 0) {
                                        updateCachedData((draft) => {
                                            draft.progress = 0.05;
                                            draft.systemLogs =
                                                draft.systemLogs + formatLogMesssageWithTimestamp(message);
                                        });
                                    }
                                    isPolling = false;
                                    jobFetchCount += 1;
                                    return;
                                }
                            }
                            if (logsFetchCount === 0) {
                                updateCachedData((draft) => {
                                    draft.progress = 0.1;
                                    draft.systemLogs =
                                        draft.systemLogs +
                                        formatLogMesssageWithTimestamp('Awaiting initialization of compute node...');
                                });
                            }
                            const nodeUrl = rewriteUrlIfDev(job.cloud_job.compute_node_url);
                            const url = `${nodeUrl}/v1/job/${job.public_id}/status/${logsFetchCount === 0 ? '?logs=full' : ''}`;
                            const response = await fetch(url);
                            const result = (await response.json()) as {
                                error_code?: number;
                                is_completed: boolean;
                                previous_status_updates?: { log_message: string; progress: number }[];
                                status_updates: { log_message: string; progress: number }[];
                                stdout_and_stderr_packages_b64: string[];
                                streamed_logs_packages_b64?: string[];
                            };

                            const logPackages = result.streamed_logs_packages_b64?.length
                                ? result.streamed_logs_packages_b64
                                : (result.stdout_and_stderr_packages_b64 ?? []);
                            const logPackagesAsString = [];
                            for (const stdout_and_stderr_package_b64 of logPackages) {
                                const stdout_and_stderr_package = base64ToByteArray(stdout_and_stderr_package_b64);
                                const { stdout, stderr } = new StdoutAndStderrPackage(
                                    stdout_and_stderr_package,
                                ).getAttributes();

                                if (stdout) {
                                    logPackagesAsString.push(textDecoder.decode(stdout));
                                }
                                if (stderr) {
                                    logPackagesAsString.push(textDecoder.decode(stderr));
                                }
                            }

                            const logPackagesAsSingleString = logPackagesAsString.join('');
                            const systemUpdates = result.previous_status_updates ?? result.status_updates;
                            const systemLogs = systemUpdates
                                .map(({ log_message }) => formatLogMesssageWithTimestamp(log_message))
                                .join('');
                            const newProgress: undefined | number = systemUpdates.pop()?.progress;
                            rawLogs += logPackagesAsSingleString;
                            const processedLogs = processStreamedOutput(rawLogs);
                            updateCachedData((draft) => {
                                draft.logs = processedLogs;
                                draft.systemLogs = draft.systemLogs + systemLogs;
                                if (newProgress) {
                                    draft.progress = newProgress / 100;
                                }
                            });

                            if (result.is_completed || result.error_code) {
                                dispatch(resultsApi.util.invalidateTags([ApiTagTypes.result]));
                                clearInterval(interval);
                            }

                            logsFetchCount += 1;
                            isPolling = false;
                        } catch (error) {
                            console.error(error);
                            dispatch(resultsApi.util.invalidateTags([ApiTagTypes.result]));
                            clearInterval(interval);
                        }
                    };
                    await pollComputeNode();
                    interval = setInterval(pollComputeNode, 2000);
                } catch (error) {
                    console.error(error);
                    // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
                    // in which case `cacheDataLoaded` will throw
                }
                await cacheEntryRemoved;
                console.log('Cache entry removed for getLogs, stopping polling');
                clearInterval(interval);
            },
        }),
        saveLogs: build.mutation<null, { jobUuid: string; jobAuthToken: string }>({
            queryFn: async (args, queryApi) => {
                const { data, error } = await queryApi.dispatch(resultsApi.endpoints.getLogs.initiate(args));
                if (error) {
                    queryApi.dispatch(notificationActions.addError(`Failed to fetch logs: ${error.error}`));
                    return { error };
                }
                try {
                    const logsAsByteArray = new TextEncoder().encode(data.logs);
                    saveFileAs(`${data.job.main_result.name}-logs.txt`, 'text/plain', logsAsByteArray);
                } catch (error) {
                    console.error('Failed to save logs to file:', error);
                    queryApi.dispatch(notificationActions.addError(`Failed to save logs to file: ${error}`));
                    return { error: { data: { message: `Failed to save logs to file: ${error}` }, status: 200 } };
                }
                return { data: null };
            },
        }),
        updateResultName: build.mutation<null, { jobUuid: string; name: string }>({
            invalidatesTags: (result, error) => (error ? [] : [ApiTagTypes.result, ApiTagTypes.experimentResults]),
            query: (args) => ({
                url: `/jobs/${args.jobUuid}/main_result/`,
                method: 'PATCH',
                body: { result_name_prefix: args.name },
            }),
        }),
        addResultToExperiment: build.mutation<null, { resultUuid: string; experimentUuid: string; redirect?: boolean }>(
            {
                invalidatesTags: [ApiTagTypes.result, ApiTagTypes.experiments, ApiTagTypes.experimentResults],
                queryFn: async (args, api, extraOptions, baseQuery) => {
                    const response = await baseQuery({
                        url: `/experiments/${args.experimentUuid}/jobs/`,
                        method: 'POST',
                        body: { job_uuid: args.resultUuid },
                    });
                    if (response.error) {
                        return { error: response.error };
                    }
                    api.dispatch(notificationActions.addSuccess('Result added to experiment'));
                    if (args.redirect) {
                        await navigate(`/experiments/${args.experimentUuid}/results/${args.resultUuid}/?view=details`);
                    }
                    return { data: null };
                },
            },
        ),
        removeResultFromExperiment: build.mutation<
            null,
            { resultUuid: string; experimentUuid: string; redirect?: boolean }
        >({
            invalidatesTags: [ApiTagTypes.result, ApiTagTypes.experiments, ApiTagTypes.experimentResults],
            queryFn: async (args, api, extraOptions, baseQuery) => {
                const response = await baseQuery({
                    url: `/experiments/${args.experimentUuid}/jobs/${args.resultUuid}/`,
                    method: 'DELETE',
                });
                if (response.error) {
                    return { error: response.error };
                }
                api.dispatch(notificationActions.addSuccess('Result removed from experiment'));
                if (args.redirect) {
                    await navigate(`/results/${args.resultUuid}/?view=details`);
                }
                return { data: null };
            },
        }),
        getNotes: build.query<
            IPaginatedResponse<IResultNote>,
            {
                jobUuid: string;
                jobAuthToken: string;
                pageSize?: number;
                page?: number;
                types?: ResultNoteType[];
            }
        >({
            providesTags: [ApiTagTypes.resultNotes],
            query: (args) => ({
                url: `/jobs/${args.jobUuid}/notes/`,
                params: {
                    page: args.page,
                    page_size: args.pageSize,
                    type: args.types ? args.types.join(',') : undefined,
                },
                headers: { 'Job-Auth-Token': args.jobAuthToken },
            }),
        }),
        createNote: build.mutation<
            IResultNote,
            { jobUuid: string; jobAuthToken?: string; note: string; type?: ResultNoteType }
        >({
            invalidatesTags: [ApiTagTypes.resultNotes],
            query: (args) => ({
                url: `/jobs/${args.jobUuid}/notes/`,
                method: 'POST',
                body: { note: args.note, type: args.type },
                headers: args.jobAuthToken ? { 'Job-Auth-Token': args.jobAuthToken } : undefined,
            }),
        }),
        updateJobNotifications: build.mutation<null, { jobUuid: string; sendNotifications: boolean }>({
            invalidatesTags: [ApiTagTypes.result],
            query: (args) => ({
                url: `/jobs/${args.jobUuid}/`,
                method: 'PATCH',
                body: { send_notifications_to_user: args.sendNotifications },
            }),
        }),
    }),
});

export const constructError = (errorMessage: string) => ({
    error: {
        error: errorMessage,
        status: 'CUSTOM_ERROR' as 'CUSTOM_ERROR',
    },
});

export const {
    useCancelResultMutation,
    useCreateNoteMutation,
    useDeleteResultMutation,
    useGetInputQuery,
    useGetLogsQuery,
    useSaveLogsMutation,
    useGetNotesQuery,
    useGetResultQuery,
    useAddResultToExperimentMutation,
    useRemoveResultFromExperimentMutation,
    useUpdateResultNameMutation,
    useUpdateJobNotificationsMutation,
} = resultsApi;

export { resultsApi };
